import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import RegisterPage from "@/pages/RegisterPage";
import UploadProofPage from "@/pages/UploadProofPage";
import QuizPage from "@/pages/QuizPage";
import TestSubmittedPage from "@/pages/TestSubmittedPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import CampaignsPage from "@/pages/CampaignsPage";
import PowerDialerPage from "@/pages/PowerDialerPage";
import SettingsPage from "@/pages/SettingsPage";
import TenantLandingPage from "@/pages/TenantLandingPage";
import SuperDashboardPage from "@/pages/SuperDashboardPage";
import SaasSalesPage from "@/pages/SaasSalesPage";
import ActivityFeedPage from "@/pages/ActivityFeedPage";
import QuickLeadFormPage from "@/pages/QuickLeadFormPage";
import BudgetCalculatorPage from "@/pages/BudgetCalculatorPage";
import UniversitiesAdminPage from "@/pages/UniversitiesAdminPage";
import LeadsPage from "@/pages/LeadsPage";
import CounselingsPage from "@/pages/CounselingsPage";
import CustomPipelinePage from "@/pages/CustomPipelinePage";
import StudentDocUploadPage from "@/pages/StudentDocUploadPage";
import WhatsAppInboxPage from "@/pages/WhatsAppInboxPage";
import DigitalWorkshopPage from "@/pages/DigitalWorkshopPage";
import { WhatsAppInboxProvider } from "@/contexts/WhatsAppInboxContext";
import AdminShell from "@/layouts/AdminShell";

function ProtectedAdmin({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/super" replace />;
  return children;
}

function ProtectedSuper({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/admin" replace />;
  return children;
}

function AdminOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user && user.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<DigitalWorkshopPage />} />
          <Route path="/scholars" element={<LandingPage />} />
          <Route path="/saas" element={<SaasSalesPage />} />
          <Route path="/digital" element={<DigitalWorkshopPage />} />
          <Route path="/t/:slug" element={<TenantLandingPage />} />
          <Route path="/lead-form" element={<QuickLeadFormPage />} />
          <Route path="/t/:slug/lead-form" element={<QuickLeadFormPage />} />
          <Route path="/budget-calculator" element={<BudgetCalculatorPage />} />
          <Route path="/t/:slug/budget-calculator" element={<BudgetCalculatorPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/upload/:leadId" element={<UploadProofPage />} />
          <Route path="/upload-docs/:token" element={<StudentDocUploadPage />} />
          <Route path="/quiz/:leadId" element={<QuizPage />} />
          <Route path="/submitted/:leadId" element={<TestSubmittedPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            element={
              <ProtectedAdmin>
                <WhatsAppInboxProvider>
                  <AdminShell />
                </WhatsAppInboxProvider>
              </ProtectedAdmin>
            }
          >
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/leads" element={<LeadsPage />} />
            <Route path="/admin/counselings" element={<CounselingsPage />} />
            <Route path="/admin/whatsapp" element={<WhatsAppInboxPage />} />
            <Route path="/admin/pipeline/:pipelineId" element={<CustomPipelinePage />} />
            <Route
              path="/admin/analytics"
              element={
                <AdminOnlyRoute>
                  <AnalyticsPage />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/admin/campaigns"
              element={
                <AdminOnlyRoute>
                  <CampaignsPage />
                </AdminOnlyRoute>
              }
            />
            <Route path="/admin/dialer" element={<PowerDialerPage />} />
            <Route path="/admin/activity" element={<ActivityFeedPage />} />
            <Route
              path="/admin/universities"
              element={
                <AdminOnlyRoute>
                  <UniversitiesAdminPage />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminOnlyRoute>
                  <SettingsPage />
                </AdminOnlyRoute>
              }
            />
          </Route>
          <Route
            path="/super"
            element={
              <ProtectedSuper>
                <SuperDashboardPage />
              </ProtectedSuper>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
